import { useEffect, useMemo, useState } from "react";
import { TABLE_ACTIONS } from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import { MainLayoutContainer } from "components/Layout";

import { Tab, Tabs } from "@mui/material";
import {
  useGetFeedbacksByCoachIdLazyQuery,
  useGetFeedbacksByStudentIdLazyQuery,
  useUpdateFeedbackMutation,
} from "graphql/__generated__/hooks";
import { useAuthUser } from "hooks/useAuthUser";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { ROLES } from "types/user";
import { TabPanel } from "components/TabPanel";
import { FeedbackTable } from "./components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { shouldHideFeedback } from "utils/feedback";

export const FeedbackPortalList = () => {
  const [deleteFeedbackModal, setDeleteFeedbackModal] = useState(false);
  const [feedbackIdToDelete, setFeedbackIdToDelete] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [value, setValue] = useState(
    searchParams.get("tab") === "drafts" ? 1 : 0,
  );
  const navigate = useNavigate();

  const { user } = useAuthUser();

  const [getFeedbacksByCoach, { data: coachData }] =
    useGetFeedbacksByCoachIdLazyQuery();
  const [getFeedbacksByStudent, { data: studentData }] =
    useGetFeedbacksByStudentIdLazyQuery();
  const [updateFeedback] = useUpdateFeedbackMutation();

  const feedbackList = useMemo<FeedbackType[]>(() => {
    return (
      (coachData?.feedback as FeedbackType[]) ||
      (studentData?.feedback as FeedbackType[]) ||
      []
    );
  }, [coachData, studentData]);

  const feedbackCounter = useMemo(() => {
    const total = feedbackList.length;
    const draft = feedbackList.filter(
      (f) => f.status === FeedbackStatus.DRAFT,
    ).length;
    const nonDraft = feedbackList.filter(
      (f) => f.status !== FeedbackStatus.DRAFT && !shouldHideFeedback(f),
    ).length;

    return { total, draft, nonDraft };
  }, [feedbackList]);

  const handleChange = (_: any, newValue: number) => {
    setValue(newValue);
    if (newValue === 1) {
      setSearchParams({ tab: "drafts" });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    setValue(searchParams.get("tab") === "drafts" ? 1 : 0);
  }, [searchParams]);

  const refetch = () => {
    if (user?.user_id && user.role === ROLES.COACH) {
      getFeedbacksByCoach({ variables: { coachId: user.user_id } }).then();
    } else if (user?.user_id && user.role === ROLES.STUDENT) {
      getFeedbacksByStudent({ variables: { studentId: user.user_id } }).then();
    }
  };

  useEffect(() => {
    if (user?.user_id && user.role) {
      refetch();
    }
  }, [user]);

  const handleActionClick = async (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.EDIT:
        navigate(`/request-feedback/${id}`);
        break;
      case TABLE_ACTIONS.ARCHIVE:
      case TABLE_ACTIONS.DELETE:
        setFeedbackIdToDelete(id);
        setDeleteFeedbackModal(true);
        break;
      default:
        break;
    }
  };

  const handleDelete = async () => {
    if (!feedbackIdToDelete || feedbackIdToDelete === "") {
      return;
    }
    await updateFeedback({
      variables: {
        id: feedbackIdToDelete,
        feedback: {
          status: FeedbackStatus.HIDDEN,
        },
      },
    });
    await refetch();
    setFeedbackIdToDelete("");
  };

  return (
    <MainLayoutContainer title="Feedback">
      <>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="Feedback Status"
        >
          <Tab
            label={`Active (${feedbackCounter.nonDraft})`}
            id="feedback-tab-0"
            aria-controls="feedback-tabpanel-0"
          />
          <Tab
            label={`Draft (${feedbackCounter.draft})`}
            id="feedback-tab-1"
            aria-controls="feedback-tabpanel-1"
          />
        </Tabs>

        <TabPanel value={value} index={0}>
          <FeedbackTable
            feedbacks={feedbackList}
            handleActionClick={handleActionClick}
            userRole={user?.role}
            excludeDraft={true}
          />
        </TabPanel>

        <TabPanel value={value} index={1}>
          <FeedbackTable
            feedbacks={feedbackList}
            handleActionClick={handleActionClick}
            userRole={user?.role}
            status={FeedbackStatus.DRAFT}
          />
        </TabPanel>

        {deleteFeedbackModal && (
          <ConfirmActionModal
            title={"Heads up!"}
            message={"Are you sure you want to delete this Feedback?"}
            show={deleteFeedbackModal}
            onConfirm={handleDelete}
            onDismiss={() => {
              setDeleteFeedbackModal(false);
            }}
          />
        )}
      </>
    </MainLayoutContainer>
  );
};
